/**
 * Leaderboards Page
 * Global, department, and team leaderboards
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import {
  Trophy, Crown, Medal, Star, TrendingUp, Users,
  ArrowLeft, Filter, Flame, Award, Target
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Category configurations
const CATEGORY_CONFIG = {
  SALES: { label: 'Sales', className: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' },
  OPS: { label: 'Operations', className: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' },
  PRODUCTION: { label: 'Production', className: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400' },
  CREWS: { label: 'Crews', className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  MARKETING: { label: 'Marketing', className: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400' },
  LEADERSHIP: { label: 'Leadership', className: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400' },
  GENERAL: { label: 'General', className: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' }
};

// Leaderboard Entry Component
const LeaderboardEntry = ({ entry, isCurrentUser }) => {
  const getRankDisplay = (rank) => {
    if (rank === 1) return (
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
        <Crown className="h-5 w-5 text-white" />
      </div>
    );
    if (rank === 2) return (
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-slate-300 to-slate-400 flex items-center justify-center">
        <Medal className="h-5 w-5 text-white" />
      </div>
    );
    if (rank === 3) return (
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-600 to-amber-700 flex items-center justify-center">
        <Medal className="h-5 w-5 text-white" />
      </div>
    );
    return (
      <div className="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
        <span className="font-bold text-lg">{rank}</span>
      </div>
    );
  };

  return (
    <div 
      className={`flex items-center gap-4 p-4 rounded-xl transition-all ${
        isCurrentUser 
          ? 'bg-blue-50 dark:bg-blue-950/30 border-2 border-blue-300 dark:border-blue-700 shadow-sm' 
          : 'hover:bg-accent/50'
      } ${entry.rank <= 3 ? 'bg-gradient-to-r from-amber-50/50 to-transparent dark:from-amber-950/10' : ''}`}
    >
      {getRankDisplay(entry.rank)}
      
      <Avatar className={`h-12 w-12 ${entry.rank <= 3 ? 'ring-2 ring-amber-400 dark:ring-amber-600' : ''}`}>
        <AvatarFallback className={entry.rank === 1 ? 'bg-amber-100 text-amber-700' : ''}>
          {entry.entity_name?.charAt(0) || '?'}
        </AvatarFallback>
      </Avatar>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-semibold truncate">{entry.entity_name}</p>
          {isCurrentUser && (
            <Badge variant="outline" className="text-xs">You</Badge>
          )}
        </div>
        <p className="text-sm text-muted-foreground">
          {entry.participation_count || 0} competitions
          {entry.active_streak > 0 && (
            <span className="ml-2 text-orange-500">
              <Flame className="h-3 w-3 inline mr-1" />
              {entry.active_streak} streak
            </span>
          )}
        </p>
      </div>
      
      <div className="text-right">
        <p className="text-2xl font-bold">{entry.total_wins}</p>
        <p className="text-xs text-muted-foreground">wins</p>
      </div>
      
      <div className="text-right">
        <p className="text-lg font-semibold text-blue-600 dark:text-blue-400">
          {entry.total_points?.toLocaleString() || 0}
        </p>
        <p className="text-xs text-muted-foreground">points</p>
      </div>
    </div>
  );
};

// Hall of Fame Entry
const HallOfFameCard = ({ entry }) => {
  const categoryConfig = CATEGORY_CONFIG[entry.category] || CATEGORY_CONFIG.GENERAL;
  
  return (
    <Card className="overflow-hidden">
      <div className="h-2 bg-gradient-to-r from-amber-400 to-orange-500" />
      <CardContent className="p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="relative">
            <Avatar className="h-12 w-12 border-2 border-amber-400">
              <AvatarFallback className="bg-amber-100 text-amber-700">
                {entry.winner_name?.charAt(0)}
              </AvatarFallback>
            </Avatar>
            <Trophy className="absolute -bottom-1 -right-1 h-5 w-5 text-amber-500 bg-white dark:bg-slate-900 rounded-full p-0.5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold truncate">{entry.winner_name}</p>
            <p className="text-sm text-muted-foreground truncate">{entry.competition_title}</p>
          </div>
        </div>
        <div className="flex items-center justify-between text-sm">
          <Badge className={categoryConfig.className}>{categoryConfig.label}</Badge>
          <span className="text-muted-foreground">
            {new Date(entry.completed_at).toLocaleDateString()}
          </span>
        </div>
        {entry.props_message && (
          <p className="mt-2 text-sm italic text-muted-foreground">"{entry.props_message}"</p>
        )}
      </CardContent>
    </Card>
  );
};

export const Leaderboards = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('wins');
  const [period, setPeriod] = useState('all');
  const [category, setCategory] = useState('all');
  const [leaderboard, setLeaderboard] = useState([]);
  const [hallOfFame, setHallOfFame] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      const [leaderboardRes, hofRes] = await Promise.all([
        axios.get(`${API}/competitions/leaderboards/global`, {
          params: {
            leaderboard_type: activeTab,
            period,
            category: category !== 'all' ? category : undefined
          }
        }),
        axios.get(`${API}/competitions/hall-of-fame`, {
          params: { limit: 10 }
        })
      ]);
      
      setLeaderboard(leaderboardRes.data?.entries || []);
      setHallOfFame(hofRes.data || []);
    } catch (error) {
      console.error('Error fetching leaderboards:', error);
      toast.error('Failed to load leaderboards');
    } finally {
      setLoading(false);
    }
  }, [activeTab, period, category]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <Layout>
      <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="leaderboards-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/competition')}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Trophy className="h-7 w-7 text-amber-500" />
              Leaderboards
            </h1>
            <p className="text-muted-foreground">See who's leading the pack</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Time Period" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="quarter">This Quarter</SelectItem>
              <SelectItem value="all">All Time</SelectItem>
            </SelectContent>
          </Select>
          
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-[150px]">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
                <SelectItem key={key} value={key}>{config.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Leaderboard */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList>
                    <TabsTrigger value="wins" className="gap-2">
                      <Trophy className="h-4 w-4" />
                      Wins
                    </TabsTrigger>
                    <TabsTrigger value="points" className="gap-2">
                      <Star className="h-4 w-4" />
                      Points
                    </TabsTrigger>
                    <TabsTrigger value="streak" className="gap-2">
                      <Flame className="h-4 w-4" />
                      Streaks
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </CardHeader>
              <CardContent className="space-y-2">
                {loading ? (
                  <div className="text-center py-8 text-muted-foreground">Loading...</div>
                ) : leaderboard.length === 0 ? (
                  <div className="text-center py-12">
                    <Trophy className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                    <h3 className="font-semibold mb-2">No rankings yet</h3>
                    <p className="text-muted-foreground">
                      Compete in competitions to appear on the leaderboard!
                    </p>
                    <Button 
                      className="mt-4"
                      onClick={() => navigate('/competition')}
                    >
                      View Competitions
                    </Button>
                  </div>
                ) : (
                  leaderboard.map(entry => (
                    <LeaderboardEntry
                      key={entry.entity_id}
                      entry={entry}
                      isCurrentUser={entry.entity_id === user?.id}
                    />
                  ))
                )}
              </CardContent>
            </Card>
          </div>

          {/* Hall of Fame Sidebar */}
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Award className="h-5 w-5 text-amber-500" />
                  Hall of Fame
                </CardTitle>
                <CardDescription>Recent competition winners</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {hallOfFame.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No winners yet. Be the first!
                  </p>
                ) : (
                  hallOfFame.slice(0, 5).map(entry => (
                    <HallOfFameCard key={entry.id} entry={entry} />
                  ))
                )}
              </CardContent>
            </Card>

            {/* Stats Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Quick Stats</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-950/20 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Target className="h-4 w-4 text-green-600" />
                    <span className="text-sm">Active Competitions</span>
                  </div>
                  <span className="font-bold text-green-600">{hallOfFame.length || 0}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-purple-50 dark:bg-purple-950/20 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-purple-600" />
                    <span className="text-sm">Total Competitors</span>
                  </div>
                  <span className="font-bold text-purple-600">{leaderboard.length}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-amber-50 dark:bg-amber-950/20 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Trophy className="h-4 w-4 text-amber-600" />
                    <span className="text-sm">Total Wins</span>
                  </div>
                  <span className="font-bold text-amber-600">
                    {leaderboard.reduce((sum, e) => sum + (e.total_wins || 0), 0)}
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Leaderboards;
